import { createContext, useContext, useEffect, useState, ReactNode } from 'react'
import { User } from '@/types'
import { buildApiUrl } from '@/config/api'
import { updateRedditUserIdentifiers } from '@/lib/reddit-conversions'
import { setUserContext, trackUserAction } from '../lib/sentry'

interface AuthContextType {
  user: User | null
  isLoading: boolean
  login: (email: string, password: string) => Promise<{ success: boolean; message: string }>
  loginWithToken: (token: string) => Promise<{ success: boolean; message: string }>
  register: (userData: {
    name: string
    email: string
    password: string
    company: string
    role: string
  }) => Promise<{ success: boolean; message: string }>
  logout: () => void
  checkAuth: () => Promise<void>
  updateUser: (userData: User) => void
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  // Helper function to set user and update Sentry context
  const setUserWithSentry = (userData: User | null) => {
    setUser(userData)
    if (userData) {
      setUserContext({
        id: userData.id,
        email: userData.email,
        name: userData.name
      })
    }
  }

  const checkAuth = async () => {
    const token = localStorage.getItem('token')
    const storedUser = localStorage.getItem('user')

    if (token && storedUser) {
      try {
        const response = await fetch(buildApiUrl('/api/auth/me'), {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        })

        if (response.ok) {
          const data = await response.json()
          let userData = data.data

          // Set user immediately with basic data
          setUserWithSentry(userData)

          // Fetch company data in background if needed and not already present
          if (userData.company && userData.company !== '' && !userData.companyName) {
            fetch(buildApiUrl(`/api/companies/${userData.company}`), {
              headers: {
                'Authorization': `Bearer ${token}`,
              },
            })
            .then(companyResponse => {
              if (companyResponse.ok) {
                return companyResponse.json()
              } else if (companyResponse.status === 404) {
                console.warn('Company not found for user:', userData.company)
                return null
              }
              throw new Error(`Company fetch failed with status: ${companyResponse.status}`)
            })
            .then(companyData => {
              if (companyData) {
                const updatedUserData = {
                  ...userData,
                  companyName: companyData.data.name
                }
                setUser(updatedUserData)
                localStorage.setItem('user', JSON.stringify(updatedUserData))
              }
            })
            .catch(companyError => {
              console.error('Failed to fetch company data:', companyError)
              // Continue without company name if fetch fails
            })
          }
        } else {
          // Token is invalid, clear storage
          localStorage.removeItem('token')
          localStorage.removeItem('user')
          setUser(null)
        }
      } catch (error) {
        console.error('Auth check failed:', error)
        localStorage.removeItem('token')
        localStorage.removeItem('user')
        setUser(null)
      }
    } else {
      setUser(null)
    }
    setIsLoading(false)
  }

  const login = async (email: string, password: string): Promise<{ success: boolean; message: string }> => {
    try {
      const response = await fetch(buildApiUrl('/api/auth/login'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      })

      const data = await response.json()

      if (data.success) {
        const { token, user: userData } = data.data
        
        // Store token and user data
        localStorage.setItem('token', token)
        localStorage.setItem('user', JSON.stringify(userData))
        
        // Set user immediately
        setUserWithSentry(userData)

        // Update Reddit pixel with user identifiers for advanced matching
        try {
          updateRedditUserIdentifiers({
            email: userData.email,
            externalId: userData.id
          })
        } catch (error) {
          console.error('Failed to update Reddit user identifiers:', error)
        }
        trackUserAction('login')
        
        // Fetch company data in background if needed
        if (userData.company && userData.company !== '' && !userData.companyName) {
          fetch(buildApiUrl(`/api/companies/${userData.company}`), {
            headers: {
              'Authorization': `Bearer ${token}`,
            },
          })
          .then(companyResponse => {
            if (companyResponse.ok) {
              return companyResponse.json()
            } else if (companyResponse.status === 404) {
              console.warn('Company not found for user:', userData.company)
              return null
            }
            throw new Error(`Company fetch failed with status: ${companyResponse.status}`)
          })
          .then(companyData => {
            if (companyData) {
              const updatedUserData = {
                ...userData,
                companyName: companyData.data.name
              }
              setUser(updatedUserData)
              localStorage.setItem('user', JSON.stringify(updatedUserData))
            }
          })
          .catch(companyError => {
            console.error('Failed to fetch company data:', companyError)
            // Continue without company name if fetch fails
          })
        }
        
        return { success: true, message: 'Login successful!' }
      } else {
        return { success: false, message: 'Invalid email or password. Please try again.' }
      }
    } catch (error) {
      console.error('Login error:', error)
      return { success: false, message: 'Network error. Please check your connection and try again.' }
    }
  }

  const loginWithToken = async (token: string): Promise<{ success: boolean; message: string }> => {
    try {
      // Store the token
      localStorage.setItem('token', token)
      
      // Verify the token by calling the /me endpoint
      const response = await fetch(buildApiUrl('/api/auth/me'), {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      })

      if (response.ok) {
        const data = await response.json()
        const userData = data.data
        
        // Store user data
        localStorage.setItem('user', JSON.stringify(userData))
        setUser(userData)
        
        // Fetch company data in background if needed
        if (userData.company && userData.company !== '' && !userData.companyName) {
          fetch(buildApiUrl(`/api/companies/${userData.company}`), {
            headers: {
              'Authorization': `Bearer ${token}`,
            },
          })
          .then(companyResponse => {
            if (companyResponse.ok) {
              return companyResponse.json()
            }
            throw new Error('Company fetch failed')
          })
          .then(companyData => {
            const updatedUserData = {
              ...userData,
              companyName: companyData.data.name
            }
            setUser(updatedUserData)
            localStorage.setItem('user', JSON.stringify(updatedUserData))
          })
          .catch(companyError => {
            console.error('Failed to fetch company data:', companyError)
            // Continue without company name if fetch fails
          })
        }
        
        return { success: true, message: '' } // Don't return message to avoid duplicate toasts
      } else {
        // Token is invalid, clear storage
        localStorage.removeItem('token')
        localStorage.removeItem('user')
        setUser(null)
        return { success: false, message: 'Invalid or expired token. Please log in again.' }
      }
    } catch (error) {
      console.error('Token login error:', error)
      localStorage.removeItem('token')
      localStorage.removeItem('user')
      setUser(null)
      return { success: false, message: 'Network error. Please check your connection and try again.' }
    }
  }

  const register = async (userData: {
    name: string
    email: string
    password: string
    company: string
    role: string
    gclid?: string
  }): Promise<{ success: boolean; message: string }> => {
    try {
      const response = await fetch(buildApiUrl('/api/auth/register'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(userData),
      })

      const data = await response.json()

      if (data.success) {
        let user = data.data.user

        // Store token and user immediately
        localStorage.setItem('token', data.data.token)
        localStorage.setItem('user', JSON.stringify(user))
        setUser(user)

        // Update Reddit pixel with user identifiers for advanced matching
        try {
          updateRedditUserIdentifiers({
            email: user.email,
            externalId: user.id
          })
        } catch (error) {
          console.error('Failed to update Reddit user identifiers:', error)
        }

        // Fetch company data in background if needed
        if (user.company && user.company !== '') {
                      fetch(buildApiUrl(`/api/companies/${user.company}`), {
            headers: {
              'Authorization': `Bearer ${data.data.token}`,
            },
          })
          .then(companyResponse => {
            if (companyResponse.ok) {
              return companyResponse.json()
            }
            throw new Error('Company fetch failed')
          })
          .then(companyData => {
            const updatedUser = {
              ...user,
              companyName: companyData.data.name
            }
            setUser(updatedUser)
            localStorage.setItem('user', JSON.stringify(updatedUser))
          })
          .catch(companyError => {
            console.error('Failed to fetch company data:', companyError)
            // Continue without company name if fetch fails
          })
        }

        return { success: true, message: 'Account created successfully!' }
      } else {
        // Handle specific error cases
        let errorMessage = 'Registration failed. Please try again.'
        
        if (response.status === 409) {
          if (data.error === 'User already exists') {
            errorMessage = 'There is already a user with this email address. Please use a different email or try logging in instead.'
          } else {
            errorMessage = 'This email is already registered. Please use a different email address.'
          }
        } else if (response.status === 400) {
          if (data.error === 'Validation error') {
            errorMessage = 'Please check your information and try again. Make sure all required fields are filled correctly.'
          } else {
            errorMessage = data.error || 'Please check your information and try again.'
          }
        } else if (response.status === 500) {
          errorMessage = 'Server error. Please try again later.'
        }
        
        return { success: false, message: errorMessage }
      }
    } catch (error) {
      console.error('Registration error:', error)
      return { success: false, message: 'Network error. Please check your connection and try again.' }
    }
  }

  const logout = () => {
    trackUserAction('logout')
    localStorage.removeItem('token')
    localStorage.removeItem('user')
    setUserWithSentry(null)
  }

  const updateUser = (userData: User) => {
    setUser(userData)
    localStorage.setItem('user', JSON.stringify(userData))
  }

  useEffect(() => {
    checkAuth()
  }, [])

  const value: AuthContextType = {
    user,
    isLoading,
    login,
    loginWithToken,
    register,
    logout,
    checkAuth,
    updateUser,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
} 